
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { File, Copy, Clock, Trash2, Download } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface Transfer {
  id: string;
  filename: string;
  original_name: string;
  size: number;
  expires_at: string;
  shared_links?: {
    access_count: number;
    id?: string;
  }[];
}

interface TransfersListProps {
  transfers: Transfer[];
  onCopy: (id: string, filename: string) => Promise<void>;
  onDownload: (filename: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}

export function TransfersList({ transfers, onCopy, onDownload, onDelete }: TransfersListProps) {
  return (
    <ScrollArea className="h-[500px] w-full rounded-lg border border-[#2E2F33] bg-[#1A1B1E] p-4">
      {transfers.length === 0 ? (
        <p className="text-center text-white/60">No hay transferencias aún</p>
      ) : (
        <div className="space-y-3">
          {transfers.map((transfer) => (
            <div
              key={transfer.id}
              className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg bg-[#0A0D14] border border-[#2E2F33] hover:border-[#3E3F43] transition-colors duration-200"
            >
              <div className="flex items-center space-x-3 mb-3 sm:mb-0">
                <File className="h-5 w-5 text-white/40" />
                <div>
                  <p className="text-sm font-medium text-white">{transfer.original_name}</p>
                  <div className="flex items-center space-x-2 mt-1">
                    <p className="text-xs text-white/60">
                      {(transfer.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                    <span className="text-white/20">•</span>
                    <div className="flex items-center text-xs text-white/60">
                      <Clock className="h-3 w-3 mr-1" />
                      Expira {formatDistanceToNow(new Date(transfer.expires_at), { addSuffix: true })}
                    </div>
                    <span className="text-white/20">•</span>
                    <div className="flex items-center text-xs text-white/60">
                      <Download className="h-3 w-3 mr-1" />
                      {transfer.shared_links?.[0]?.access_count || 0} descargas
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCopy(transfer.id, transfer.filename)}
                  className="w-full sm:w-auto bg-[#1A1B1E] border-[#2E2F33] text-white hover:bg-[#2E2F33] hover:text-white transition-colors"
                >
                  <Copy className="h-4 w-4 mr-2" />
                  Share
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDownload(transfer.filename)}
                  className="w-full sm:w-auto bg-[#1A1B1E] border-[#2E2F33] text-white hover:bg-[#2E2F33] hover:text-white transition-colors"
                >
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(transfer.id)}
                  className="w-full sm:w-auto bg-[#1A1B1E] border-[#2E2F33] text-red-400 hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/50 transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </ScrollArea>
  );
}
